import { create } from 'zustand'
import axios from 'axios'

const API_BASE = import.meta.env.PROD ? '/api' : 'http://localhost:9099/api'

interface AuthState {
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  authEnabled: boolean | null // null = not yet checked
  username: string | null
  role: string | null
  allowedPages: string[]
  loginError: string | null
  loading: boolean

  // Actions
  checkAuthStatus: () => Promise<void>
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  refreshAccessToken: () => Promise<boolean>
  restoreSession: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: localStorage.getItem('auth_token'),
  refreshToken: localStorage.getItem('auth_refresh_token'),
  isAuthenticated: false,
  authEnabled: null,
  username: null,
  role: null,
  allowedPages: [],
  loginError: null,
  loading: true,

  checkAuthStatus: async () => {
    try {
      const response = await axios.get(`${API_BASE}/auth/status`)
      const authEnabled = response.data.auth_enabled
      set({ authEnabled })

      if (!authEnabled) {
        // Auth disabled — everyone is authenticated with full access
        set({
          isAuthenticated: true,
          role: 'admin',
          allowedPages: ['dashboard', 'monitoring', 'backtest', 'positions', 'settings'],
          loading: false,
        })
      }
    } catch {
      // If we can't reach the server, assume auth is enabled
      set({ authEnabled: true, loading: false })
    }
  },

  login: async (username: string, password: string) => {
    set({ loginError: null, loading: true })
    try {
      const response = await axios.post(`${API_BASE}/auth/login`, { username, password })
      const { access_token, refresh_token, role, allowed_pages } = response.data

      localStorage.setItem('auth_token', access_token)
      localStorage.setItem('auth_refresh_token', refresh_token)

      set({
        token: access_token,
        refreshToken: refresh_token,
        isAuthenticated: true,
        username: response.data.username,
        role,
        allowedPages: allowed_pages,
        loginError: null,
        loading: false,
      })
      return true
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Login failed'
      set({ loginError: message, loading: false })
      return false
    }
  },

  logout: () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_refresh_token')
    set({
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      username: null,
      role: null,
      allowedPages: [],
      loginError: null,
      loading: false,
    })
  },

  refreshAccessToken: async () => {
    const { refreshToken } = get()
    if (!refreshToken) return false

    try {
      const response = await axios.post(`${API_BASE}/auth/refresh`, {
        refresh_token: refreshToken,
      })
      const { access_token, role, allowed_pages, username } = response.data

      localStorage.setItem('auth_token', access_token)
      set({
        token: access_token,
        isAuthenticated: true,
        username,
        role,
        allowedPages: allowed_pages,
      })
      return true
    } catch {
      // Refresh failed — force logout
      get().logout()
      return false
    }
  },

  restoreSession: async () => {
    const { token, authEnabled } = get()

    // First check if auth is enabled
    if (authEnabled === null) {
      await get().checkAuthStatus()
    }

    // If auth is disabled, we're already set up
    if (!get().authEnabled) return

    // No token stored — not authenticated
    if (!token) {
      set({ loading: false })
      return
    }

    // Validate token by calling /auth/me
    try {
      const response = await axios.get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.data.auth_enabled === false) {
        set({
          isAuthenticated: true,
          role: 'admin',
          allowedPages: ['dashboard', 'monitoring', 'backtest', 'positions', 'settings'],
          loading: false,
        })
        return
      }

      set({
        isAuthenticated: true,
        username: response.data.username,
        role: response.data.role,
        allowedPages: response.data.allowed_pages,
        loading: false,
      })
    } catch {
      // Token invalid — try refresh
      const refreshed = await get().refreshAccessToken()
      if (!refreshed) {
        set({ loading: false })
      }
    }
  },
}))

// ---------------------------------------------------------------------------
// Axios interceptors — attach token and handle 401s
// ---------------------------------------------------------------------------

// Request interceptor: attach Bearer token
axios.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Response interceptor: handle 401 by refreshing token
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string | null) => void
  reject: (error: any) => void
}> = []

const processQueue = (token: string | null, error: any = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token)
  })
  failedQueue = []
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Don't retry auth endpoints or already-retried requests
    if (
      error.response?.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/refresh') ||
      originalRequest.url?.includes('/auth/status')
    ) {
      return Promise.reject(error)
    }

    if (isRefreshing) {
      // Queue this request while refresh is in progress
      return new Promise((resolve, reject) => {
        failedQueue.push({
          resolve: (token) => {
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`
            }
            resolve(axios(originalRequest))
          },
          reject,
        })
      })
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const refreshed = await useAuthStore.getState().refreshAccessToken()
      if (refreshed) {
        const newToken = useAuthStore.getState().token
        processQueue(newToken)
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return axios(originalRequest)
      } else {
        processQueue(null, error)
        return Promise.reject(error)
      }
    } catch (refreshError) {
      processQueue(null, refreshError)
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)
